import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, Lock, Mail, ChevronDown, Sparkles } from "lucide-react";

// Static fallback used if the /api/public/demo-accounts endpoint is unreachable.
// The live payload (which resolves site-linked labels from the DB) is preferred
// so future site renames flow through without a code change.
const DEMO_GROUPS_FALLBACK = [
    {
        key: "head-office",
        title: "Head Office",
        accounts: [
            { email: "demo.admin@flexworx.in",       label: "HO Admin",            hint: "All sites, everything" },
            { email: "demo.pm@flexworx.in",          label: "Project Manager",     hint: "All sites, is_head → financial access" },
            { email: "demo.procurement@flexworx.in", label: "Procurement Officer", hint: "All sites — vendors, PRs, requisitions, price comparison. NO income/margins/salaries." },
        ],
    },
    {
        key: "managed-sites",
        title: "Managed sites (Facility Heads)",
        accounts: [
            { email: "demo.manager@flexworx.in", label: "SMIFS FH",    hint: "SMIFS · managed-client" },
            { email: "demo.bayer@flexworx.in",   label: "Bayer FH",    hint: "Bayer" },
            { email: "demo.bosch@flexworx.in",   label: "Bosch FH",    hint: "Bosch" },
            { email: "demo.merck@flexworx.in",   label: "Susonity FH", hint: "Susonity · all lines recoverable" },
            { email: "demo.nuvama@flexworx.in",  label: "Nuvama FH",   hint: "Nuvama · 52-task template" },
            { email: "demo.roche@flexworx.in",   label: "Roche FH",    hint: "Roche" },
        ],
    },
    {
        key: "buildings",
        title: "Buildings (Own)",
        accounts: [
            { email: "demo.building@flexworx.in", label: "Centrum FH",  hint: "P.S. Arcadia Centrum · CAM/salary/petty" },
            { email: "demo.central@flexworx.in",  label: "Central FH",  hint: "P.S. Arcadia Central · empty registers" },
            { email: "demo.centre@flexworx.in",   label: "Centre FH",   hint: "Arcadia Centre · empty registers" },
        ],
    },
    {
        key: "coworking",
        title: "Coworking",
        accounts: [
            { email: "demo.coworking@flexworx.in", label: "Coworking Lead", hint: "Flexworx BC · sales & snack stock" },
            { email: "demo.counter@flexworx.in",   label: "Counter Staff",  hint: "Sales entry only" },
        ],
    },
    {
        key: "assistant",
        title: "Assistant",
        accounts: [
            { email: "demo.assistant@flexworx.in", label: "Assistant", hint: "SMIFS · own tasks + KRA scorecard" },
        ],
    },
    {
        key: "client",
        title: "Client (external)",
        accounts: [
            { email: "demo.client@flexworx.in", label: "SMIFS Client", hint: "Read-only portal — reports, SR summary, compliance chips" },
        ],
    },
];
const DEMO_PASSWORD_FALLBACK = "Demo@123";

export default function LoginPage() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [demoOpen, setDemoOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState("managed-sites");
    const [demoGroups, setDemoGroups] = useState(DEMO_GROUPS_FALLBACK);
    const [demoPassword, setDemoPassword] = useState(DEMO_PASSWORD_FALLBACK);

    useEffect(() => {
        // Best-effort: pull the live demo-account list so site-linked labels
        // (e.g. Susonity FH) always reflect the current site name in the DB.
        const base = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${base}/api/public/demo-accounts`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (data && Array.isArray(data.groups) && data.groups.length) {
                    setDemoGroups(data.groups);
                    if (data.password) setDemoPassword(data.password);
                }
            })
            .catch(() => { /* keep static fallback */ });
    }, []);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email.trim(), password);
        setSubmitting(false);
        if (res.ok) {
            if (res.user.must_change_password) nav("/change-password", { replace: true });
            else if (res.user.effective_role === "OWNER" || res.user.effective_role === "HEAD") nav("/command", { replace: true });
            else if (res.user.effective_role === "PROCUREMENT_OFFICER") nav("/procurement", { replace: true });
            else if (res.user.effective_role === "CLIENT") nav("/client", { replace: true });
            else nav("/home", { replace: true });  // role-aware landing router
        } else {
            setError(res.error);
        }
    }

    function fillDemo(acc) {
        setEmail(acc.email);
        setPassword(demoPassword);
        setError("");
    }

    return (
        <div className="min-h-screen bg-[#F4F6F9] flex flex-col md:flex-row" data-testid="login-page">
            <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-[#1E3355]">
                <div
                    className="absolute inset-0 opacity-30 bg-cover bg-center"
                    style={{ backgroundImage: "url('https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg')" }}
                />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#E02B2B] flex items-center justify-center font-display font-bold">F</div>
                        <div>
                            <div className="font-display font-bold text-lg">Flexworx Ops</div>
                            <div className="text-xs text-white/70">A Flexworx Company</div>
                        </div>
                    </div>
                    <div>
                        <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
                            Managed office services.<br />
                            Precision on every floor.
                        </h1>
                        <p className="mt-4 text-white/70 max-w-md">
                            Daily checklists, escalations, sign-off audit trail. Built for HAG Realty LLP.
                        </p>
                    </div>
                    <div className="text-xs text-white/50">
                        © {new Date().getFullYear()} HAG Realty LLP · Kolkata
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="md:hidden mb-8 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#1E3355] flex items-center justify-center text-white font-display font-bold">
                            <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-[#1E3355]">Flexworx Ops</div>
                            <div className="text-[11px] text-gray-500">A Flexworx Company</div>
                        </div>
                    </div>

                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] tracking-tight">Sign in to continue</h2>
                    <p className="mt-1 text-sm text-gray-500">Use the credentials issued by your HO administrator.</p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="relative">
                                <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white"
                                    placeholder="you@flexworxcare.com"
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                            <div className="relative">
                                <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white"
                                    placeholder="••••••••"
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>
                        {error && (
                            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-[#E02B2B]" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-[#1E3355] hover:bg-[#15243C] text-white font-medium py-2.5 rounded-md transition-colors active:scale-[0.99] disabled:opacity-60"
                            data-testid="login-submit"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-8 text-xs text-gray-500">
                        No public signup. Contact your HO administrator for access.
                    </div>

                    <div className="mt-6 rounded-md border border-gray-200 bg-white" data-testid="demo-accounts-panel">
                        <button
                            type="button"
                            onClick={() => setDemoOpen((v) => !v)}
                            className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                            data-testid="demo-accounts-toggle"
                        >
                            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#1E3355]">
                                <Sparkles className="w-4 h-4 text-[#E02B2B]" /> Demo accounts
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${demoOpen ? "rotate-180" : ""}`} />
                        </button>
                        {demoOpen && (
                            <div className="border-t border-gray-100 px-2 py-2">
                                <div className="px-1 pb-2 text-[11px] text-gray-500">
                                    Tap to fill · password <code className="bg-gray-100 px-1 rounded">{demoPassword}</code>
                                </div>
                                <div className="space-y-1">
                                    {demoGroups.map((g) => {
                                        const isOpen = openGroup === g.key;
                                        return (
                                            <div key={g.key} className="rounded-md border border-gray-100" data-testid={`demo-group-${g.key}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenGroup(isOpen ? null : g.key)}
                                                    className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-[#F4F6F9] rounded-md"
                                                    data-testid={`demo-group-toggle-${g.key}`}
                                                >
                                                    <span className="text-xs font-semibold text-[#1E3355] uppercase tracking-wider">{g.title}</span>
                                                    <span className="text-[10px] text-gray-400">{g.accounts.length} {g.accounts.length === 1 ? "acct" : "accts"} <ChevronDown className={`w-3 h-3 inline transition-transform ${isOpen ? "rotate-180" : ""}`} /></span>
                                                </button>
                                                {isOpen && (
                                                    <ul className="border-t border-gray-100 p-1 space-y-0.5">
                                                        {g.accounts.map((a) => (
                                                            <li key={a.email}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => fillDemo(a)}
                                                                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-[#F4F6F9] transition-colors"
                                                                    data-testid={`demo-fill-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                                                                >
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="text-sm font-medium text-[#1E3355]">{a.label}</span>
                                                                        <span className="text-[10px] text-gray-400 truncate">{a.email}</span>
                                                                    </div>
                                                                    <div className="text-[11px] text-gray-500 mt-0.5">{a.hint}</div>
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
